import { useCallback, useRef } from "react";

import { UseFormReturn } from "react-hook-form";

import { trpc } from "@/common/helpers/trpc";
import { TicketingOutput } from "@/common/types/trpc";
import { useStableLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { mapDataToEditRates } from "@/features/LiveFormEditor/Steps/helpers/mapper";
import { useUploadRemainingRatePhotos } from "@/features/LiveFormEditor/Steps/helpers/useUploadRemainingRatePhotos";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

interface UseSaveRateOnCollapseParams {
  form: UseFormReturn<StepTicketsFormData>;
  apiTicketing: TicketingOutput;
  finalizeImageEditor: (rateId: string) => Promise<void>;
}

export const useSaveRateOnCollapse = ({ form, apiTicketing, finalizeImageEditor }: UseSaveRateOnCollapseParams) => {
  const updateRatesAndSeats = trpc.updateTicketingRatesAndSeats.useMutation();
  const utils = trpc.useUtils();
  const uploadRemainingRatePhotos = useUploadRemainingRatePhotos();
  const { updateApiFormObject } = useStableLiveFormEditorContext<"ticketing">();

  const pendingSaveRef = useRef<Promise<void>>(Promise.resolve());

  const saveRate = useCallback(
    (rateId: string): Promise<void> => {
      const currentIndexOf = (id: string) => (form.getValues("rates") ?? []).findIndex((rate) => rate.id === id);

      const run = async () => {
        const rateIndex = currentIndexOf(rateId);
        if (rateIndex === -1) {
          return;
        }

        const isRateDirty = form.getFieldState(`rates.${rateIndex}`).isDirty;
        const isSeatsDirty = form.getFieldState("seats").isDirty;
        if (!isRateDirty && !isSeatsDirty) {
          return;
        }

        const hadSubmitted = form.formState.submitCount > 0;
        const isValid = await form.trigger(["seats", `rates.${rateIndex}`]);
        if (!isValid) {
          if (!hadSubmitted) {
            form.clearErrors(["seats", `rates.${rateIndex}`]);
          }
          return;
        }

        await finalizeImageEditor(rateId);

        const indexBeforeUpload = currentIndexOf(rateId);
        if (indexBeforeUpload === -1) {
          return;
        }
        await uploadRemainingRatePhotos({
          rates: [form.getValues(`rates.${indexBeforeUpload}`)],
          setPhotoUrl: (_rateIndex, photoIndex, photoUrl) => {
            const writeIndex = currentIndexOf(rateId);
            if (writeIndex === -1) {
              return;
            }
            form.setValue(`rates.${writeIndex}.photos.${photoIndex}.photoUrl` as const, photoUrl, {
              shouldDirty: true,
            });
          },
        });

        const mappedRates = mapDataToEditRates(form.getValues("rates") ?? [], apiTicketing);
        const isExistingRate = (apiTicketing.rates ?? []).some((rate) => rate.id === rateId);
        const ratePayload = mappedRates
          .filter((rate) => rate.id === rateId)
          .map((rate) => (isExistingRate ? { ...rate, sortIndex: undefined } : rate));
        if (ratePayload.length === 0) {
          return;
        }

        const seatsValue = form.getValues("seats");
        const { data, error } = await updateRatesAndSeats.mutateAsync({
          ticketingId: apiTicketing.id,
          seats: seatsValue ? parseInt(seatsValue, 10) : null,
          rates: ratePayload,
          ratesToDelete: [],
        });

        if (error || !data) {
          return;
        }

        await utils.getTicketingQuestionsStepData.invalidate({ ticketingId: apiTicketing.id });
        updateApiFormObject(data);
      };

      const next = pendingSaveRef.current.then(run, run);
      pendingSaveRef.current = next.catch(() => undefined);
      return next;
    },
    [
      form,
      apiTicketing,
      finalizeImageEditor,
      uploadRemainingRatePhotos,
      updateRatesAndSeats,
      utils,
      updateApiFormObject,
    ]
  );

  const waitForPendingRateSave = useCallback(() => pendingSaveRef.current, []);

  return { saveRate, waitForPendingRateSave };
};
